export interface Testimonial {
  id: number;
  name: string;
  role: string;
  story: string;
  location: string;
  imageUrl: string;
}

export const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Mara Santos',
    role: 'Remote Software Developer',
    location: 'Cebu City',
    story: 'I earn more than I ever have, but I used to wake up anxious about bills. Now, for the first time, I feel like my money has a purpose. The peace is something I cannot describe.',
    imageUrl: 'https://readdy.ai/api/search-image?query=Warm%20portrait%20of%20a%20Filipina%20woman%20in%20her%20late%20twenties%20with%20a%20peaceful%20smile%20soft%20natural%20lighting%20indoor%20calm%20atmosphere%20editorial%20photography%20style%20muted%20warm%20tones%20minimalist%20background%20soft%20focus%20professional%20headshot%20serene%20expression&width=400&height=400&seq=1&orientation=squarish',
  },
  {
    id: 2,
    name: 'Ricardo Tan',
    role: 'OFW in Dubai',
    location: 'Overseas, from Manila',
    story: 'For ten years, I sent money home and felt nothing. Now I am building something my family will remember. This movement taught me that stewardship is love in action.',
    imageUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20Filipino%20man%20in%20his%20forties%20with%20kind%20wise%20eyes%20soft%20warm%20lighting%20indoor%20natural%20environment%20calm%20atmosphere%20editorial%20photography%20style%20muted%20earth%20tones%20peaceful%20expression%20professional%20headshot%20soft%20background&width=400&height=400&seq=2&orientation=squarish',
  },
  {
    id: 3,
    name: 'Lina Reyes',
    role: 'Mother of Three',
    location: 'Quezon City',
    story: 'With three children, finances felt like survival. Now I see my money serving our family values. I do not chase wealth. I chase peace — and I am finding it.',
    imageUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20Filipina%20mother%20in%20her%20mid-thirties%20with%20gentle%20warm%20expression%20soft%20natural%20lighting%20indoor%20setting%20calm%20peaceful%20atmosphere%20editorial%20photography%20style%20muted%20warm%20tones%20professional%20headshot%20soft%20focus&width=400&height=400&seq=3&orientation=squarish',
  },
  {
    id: 4,
    name: 'Antonio Garcia',
    role: 'Retiree',
    location: 'Bacolod',
    story: 'I spent forty years earning. I wish someone had taught me to practice stewardship when I was young. Now I teach my grandchildren. The legacy is the practice.',
    imageUrl: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20Filipino%20elderly%20man%20in%20his%20sixties%20with%20gentle%20wise%20smile%20soft%20warm%20lighting%20indoor%20calm%20atmosphere%20editorial%20photography%20style%20muted%20earth%20tones%20peaceful%20expression%20professional%20headshot%20soft%20background&width=400&height=400&seq=4&orientation=squarish',
  },
];

export interface PhilosophyPrinciple {
  id: number;
  title: string;
  description: string;
  icon: string;
}

export const philosophyPrinciples: PhilosophyPrinciple[] = [
  {
    id: 1,
    title: 'Money is a tool, not a master.',
    description: 'When money serves you, life opens up. When you serve money, life narrows. We teach you how to hold it loosely but steward it faithfully.',
    icon: 'ri-hand-heart-line',
  },
  {
    id: 2,
    title: 'Visibility creates confidence.',
    description: 'You cannot steward what you cannot see. Gentle, honest visibility into your financial life is the foundation of peace.',
    icon: 'ri-eye-line',
  },
  {
    id: 3,
    title: 'Peace matters more than appearance.',
    description: 'We do not measure success by what others see. We measure it by the quiet confidence you feel when your money aligns with your values.',
    icon: 'ri-heart-line',
  },
  {
    id: 4,
    title: 'Technology should return time to people.',
    description: 'Our systems are designed to simplify, not complicate. Financial stewardship should feel like breathing room, not another task on your list.',
    icon: 'ri-time-line',
  },
  {
    id: 5,
    title: 'Stewardship is practiced, not consumed.',
    description: 'You do not need more financial advice. You need a practice. Small, consistent actions that compound into a life well-stewarded.',
    icon: 'ri-seedling-line',
  },
];

export interface ProblemItem {
  id: number;
  label: string;
  description: string;
}

export const problems: ProblemItem[] = [
  {
    id: 1,
    label: 'Earning enough, never enough',
    description: 'Paycheck-to-paycheck cycles despite decent income. The numbers look fine. The feeling never is.',
  },
  {
    id: 2,
    label: 'Financial anxiety that never sleeps',
    description: 'Waking up with a quiet dread about money. Not because there is none — because there is no plan.',
  },
  {
    id: 3,
    label: 'Endless advice, no practice',
    description: 'You have consumed a hundred financial tips. You have practiced almost none. Information is not transformation.',
  },
  {
    id: 4,
    label: 'Sacrificing purpose for survival',
    description: 'Working harder to keep up, but losing the people and causes that make it worth it. Money should serve life, not consume it.',
  },
];

export interface TransformationState {
  from: string;
  to: string;
}

export const transformations: TransformationState[] = [
  { from: 'Anxious', to: 'Peaceful' },
  { from: 'Overwhelmed', to: 'Intentional' },
  { from: 'Avoidant', to: 'Grounded' },
  { from: 'Trapped', to: 'Generous' },
];

export interface PlatformFeature {
  id: number;
  title: string;
  description: string;
  icon: string;
}

export const platformFeatures: PlatformFeature[] = [
  {
    id: 1,
    title: 'Intentional Financial Tracking',
    description: 'Not a dashboard that overwhelms you. A gentle view that shows what matters without shouting.',
    icon: 'ri-pie-chart-line',
  },
  {
    id: 2,
    title: 'Practice-Based Systems',
    description: 'Small daily and weekly rhythms that build stewardship habits. Not a system to learn — a practice to live.',
    icon: 'ri-calendar-check-line',
  },
  {
    id: 3,
    title: 'AI as Quiet Support',
    description: 'Invisible intelligence that nudges you forward. No chatbots. No noise. Just gentle guidance when you need it.',
    icon: 'ri-brain-line',
  },
  {
    id: 4,
    title: 'Goal Alignment',
    description: 'Connect every peso to what matters — family, legacy, faith, freedom. Your money should know your values.',
    icon: 'ri-flag-line',
  },
  {
    id: 5,
    title: 'Stewardship Rhythms',
    description: 'Weekly reflections, monthly reviews, and gentle reminders that keep your practice alive without overwhelming you.',
    icon: 'ri-refresh-line',
  },
];

export interface EssayItem {
  id: number;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
}

export const essays: EssayItem[] = [
  {
    id: 1,
    title: 'The Quiet Art of Enough',
    excerpt: 'What if financial peace is not about having more, but about needing less? An essay on the countercultural practice of sufficiency in a world of endless want.',
    category: 'Stewardship',
    readTime: '8 min read',
  },
  {
    id: 2,
    title: 'Money and the Filipino Family',
    excerpt: 'For OFWs and breadwinners, money carries the weight of love, sacrifice, and expectation. How do we steward it without being consumed by it?',
    category: 'Culture & Faith',
    readTime: '10 min read',
  },
  {
    id: 3,
    title: 'Why I Stopped Budgeting and Started Practicing',
    excerpt: 'Budgets are about restriction. Practices are about intention. The difference is not semantic — it is spiritual.',
    category: 'Practical',
    readTime: '6 min read',
  },
  {
    id: 4,
    title: 'A Dashboard Should Feel Like Home',
    excerpt: 'On designing financial tools that do not scream at you. Why calm interfaces matter for anxious hearts.',
    category: 'Design & Soul',
    readTime: '7 min read',
  },
];